import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '../../core/services/auth.service';
import { ToastService } from '../../core/services/toast.service';
import { environment } from 'src/environments/environment';
import { FormInputComponent } from 'src/app/shared/components/form-input/form-input.component';
import { Auth } from '@angular/fire/auth';
import { parsePhoneNumberWithError } from 'libphonenumber-js';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';
import { US_STATES } from 'src/app/core/models';

/**
 * Profile Page Component
 *
 * Displays and allows editing of the current user's profile information.
 */
@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterLink, FormInputComponent, MaxLengthDirective],
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss']
})
export class ProfileComponent implements OnInit {
  private authService = inject(AuthService);
  private toastService = inject(ToastService);
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private http = inject(HttpClient);
  private auth = inject(Auth);
  private apiUrl = environment.apiUrl;

  usStates = US_STATES;
  profileForm!: FormGroup;
  isLoading = false;
  isSaving = false;
  isEditMode = false;
  phoneVerified = false;
  emailVerified = false;

  // User data
  userEmail = '';
  userDisplayName = '';
  userPhone = '';

  ngOnInit(): void {
    this.initializeForm();
    this.loadProfile();

    if (history.state?.verified) {
      this.toastService.success('Phone number verified');
    }
  }

  verifyPhone(): void {
    // Older profile saves stored bare national digits; Firebase needs E.164.
    try {
      const phone = parsePhoneNumberWithError(this.userPhone, 'US').number;
      this.router.navigate(['/verify/phone'], { state: { phone, next: '/profile' } });
    } catch {
      this.toastService.error('Your saved phone number is invalid. Message an admin to update it');
    }
  }

  /**
   * Initialize the profile form
   */
  private initializeForm(): void {
    this.profileForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      phone: [{ value: '', disabled: true }],
      dateOfBirth: [''],
      address: [''],
      city: [''],
      state: [''],
      zipCode: ['']
    });
  }

  /**
   * Load user profile data
   */
  private async loadProfile(): Promise<void> {
    this.isLoading = true;

    try {
      // Get current user info from auth service
      const user = this.authService.getCurrentUser();
      console.log(user);
      if (user) {
        this.userEmail = user.email || '';
        this.userDisplayName = user.displayName || '';

        // Parse display name into first/last
        if (user.displayName) {
          const names = user.displayName.split(' ');
          this.profileForm.patchValue({
            firstName: names[0] || '',
            lastName: names.slice(1).join(' ') || ''
          });
        }
      }

      // Load profile from API
      const response = await firstValueFrom(
        this.http.get<any>(`${this.apiUrl}/auth/me`)
      );

      if (response.success && response.user?.profile) {
        const profile = response.user.profile;
        this.profileForm.patchValue({
          firstName: profile.firstName || '',
          lastName: profile.lastName || '',
          phone: profile.phone || '',
          dateOfBirth: profile.dateOfBirth ? profile.dateOfBirth.split('T')[0] : '',
          address: profile.address || '',
          city: profile.city || '',
          state: profile.state || '',
          zipCode: profile.zipCode || ''
        });
        this.emailVerified = profile.emailVerified;
        this.phoneVerified = !!this.auth.currentUser?.phoneNumber;
        this.userPhone = profile.phone || '';
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Toggle edit mode
   */
  toggleEditMode(): void {
    this.isEditMode = !this.isEditMode;

    if (!this.isEditMode) {
      // Reset form if canceling edit
      this.loadProfile();
    }
  }

  /**
   * Save profile changes
   */
  async saveProfile(): Promise<void> {
    if (this.profileForm.invalid) {
      this.profileForm.markAllAsTouched();
      return;
    }

    this.isSaving = true;

    try {
      const formData = this.profileForm.value;

      const response = await firstValueFrom(
        this.http.put<any>(`${this.apiUrl}/auth/profile`, {
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone || undefined,
          dateOfBirth: formData.dateOfBirth || undefined,
          address: formData.address || undefined,
          city: formData.city || undefined,
          state: formData.state || undefined,
          zipCode: formData.zipCode || undefined
        })
      );

      if (response.success || !response.error) {
        this.toastService.success('Profile updated successfully');
        this.isEditMode = false;

        // Refresh the auth token to get updated display name
        await this.authService.refreshToken();
      } else {
        throw new Error(response.error || 'Failed to update profile');
      }
    } catch (error: any) {
      console.error('Error saving profile:', error);
      const message = error?.error?.error || error?.message || 'Failed to save profile';
      this.toastService.error(message);
    } finally {
      this.isSaving = false;
    }
  }

  /**
   * Navigate back to dashboard
   */
  goBack(): void {
    this.router.navigate(['/dashboard']);
  }
}
